import { Component, AfterViewInit, ViewChild, ElementRef, Input, OnChanges, SimpleChanges, NgZone, Output, EventEmitter } from "@angular/core";
import { Chart, ChartConfiguration, ChartData } from "chart.js";
import { BaseComponent } from "../../../common/baseComponent";

export class LineChartDataset {
    public label: string;
    public data: number[];

    constructor(label: string, data: number[]) {
        this.label = label;
        this.data = data;
    }
}

export class LineChartData {
    public xLabels: string[];
    public datasets: LineChartDataset[];

    constructor(xLabels: string[], datasets: LineChartDataset[]) {
        this.xLabels = xLabels;
        this.datasets = datasets;
    }
}

export class LegendData {
    public color: string;
    public text: string;

    constructor(color: string, text: string) {
        this.color = color;
        this.text = text;
    }
}

export class ClickData {
    public datasetIndices: number[];
    public dataIndices: number[];
    constructor(data: any[]) {
        this.datasetIndices = data.map<number>(x => x._datasetIndex);
        this.dataIndices = data.map<number>(x => x._index);
    }
}

@Component({
    selector: "line-chart",
    templateUrl: "./lineChart.component.html",
    styleUrls: ["./lineChart.component.css"],
    standalone: false
})
export class LineChartComponent extends BaseComponent implements AfterViewInit, OnChanges {
    @ViewChild("chartHost", { read: ElementRef }) private chartHost: ElementRef;
    private context: any;
    private chart: any;
    private readonly colorPalette: any[] = [{ borderColor: "#2175D9", backgroundColor: "2175D9" }];
    private hostInitialHeight: string;

    @Input() public data: LineChartData;
    public legends: LegendData[];
    @Input() public isVisible: boolean;
    @Output() public clickData: EventEmitter<ClickData> = new EventEmitter<ClickData>();

    constructor(zone: NgZone) {
        super(zone);
    }

    public ngAfterViewInit(): void {
        this.hostInitialHeight = this.chartHost.nativeElement.style.height;
        this.context = this.chartHost.nativeElement.getContext("2d");

        let { data, legends } = this.generateData(this.data);

        setTimeout(() => this.legends = legends, 0);

        let maxValue = data ? data.datasets.reduce<number>((acc, x) => {
            let localMax = (x.data as Array<number>).reduce<number>((acc2, x2) => { return acc2 < x2 ? x2 : acc2; }, 0);
            return acc < localMax ? localMax : acc;
        }, 0) : 0;

        let step = Math.max(5, Math.floor(maxValue / 3));

        this.chart = new Chart(this.context, {
            type: 'line',
            data: data,
            options: {
                legend: {
                    display: false
                },
                responsive: true,
                maintainAspectRatio: false,
                fill: false,
                scales: {
                    xAxes: [{
                        gridLines: {
                            drawOnChartArea: false
                        }
                    }],
                    yAxes: [{
                        type: "linear",
                        ticks: {
                            min: 0,
                            max: maxValue,
                            stepSize: step
                        } as any,
                        afterTickToLabelConversion: (scale: any) => {
                            if (scale.ticks.length > 2) {
                                if (scale.ticks[0] - scale.ticks[1] < step) {
                                    scale.ticks[1] = null;
                                    scale.ticksAsNumbers[1] = null;
                                }
                            }
                        }
                    }]
                },
                elements: {
                    line: {
                        tension: 0, // disables bezier curves
                    }
                },
                onClick: (e, arg) => { this.clickData.emit(new ClickData(arg)); }
            }
        } as ChartConfiguration);
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (this.chart) {
            let dataChanges = changes["data"];
            if (dataChanges) {
                let { data, legends } = this.generateData(dataChanges.currentValue);
                this.chart.options.scales.yAxes[0].ticks.min = 0;
                this.chart.options.scales.yAxes[0].ticks.max = data ? data.datasets.reduce<number>((acc, x) => {
                    let localMax = (x.data as Array<number>).reduce<number>((acc2, x2) => { return acc2 < x2 ? x2 : acc2; }, 0);
                    return acc < localMax ? localMax : acc;
                }, 0) : 0;
                this.chart.data = data;
                this.chart.update();
                this.legends = legends;
            }

            let isVisibleChanges = changes["isVisible"];
            if (isVisibleChanges && isVisibleChanges.currentValue) {
                this.chartHost.nativeElement.style.height = this.hostInitialHeight;
            }
        }
    }

    private generateData(data: LineChartData): { data: ChartData, legends: LegendData[] } {
        if (data) {
            let legends: LegendData[] = [];
            let datasets: LineChartDataset[] = data.datasets;

            let processedDatasets = [];

            for (let i = 0; i < datasets.length; ++i) {
                let colorData = this.colorPalette[i % this.colorPalette.length];
                legends.push(new LegendData(colorData.backgroundColor, datasets[i].label));
                processedDatasets.push({
                    label: datasets[i].label,
                    fill: false,
                    borderColor: colorData.borderColor,
                    backgroundColor: colorData.backgroundColor,
                    data: datasets[i].data
                });
            }

            let processedData = {
                labels: data.xLabels,
                datasets: processedDatasets
            };

            return { data: processedData, legends: legends };
        } else {
            return { data: null, legends: [] };
        }
    }
}